import pick from 'lodash/pick';

import { logger } from '@/services/logger';
import { DocumentType, LibraryItem } from '@d-id/types';
import { keyframes } from '@emotion/react';
import JSZip from 'jszip';

import { checkProductMatch, PlanGroup, ProductType } from '@d-id/types';
import { AppRoutes, WordLevelTimestamp } from './types';

export function joinFunction(...functions: (() => void)[]) {
    return () => {
        functions.forEach(func => func());
    };
}
export function stopPropagation(e: React.MouseEvent) {
    e.stopPropagation();
}

const D_ID_EMAIL_SUFFIXES = ['@d-id.com', '@deidentification.co'];
export const isDidUser = (email: string | undefined) => {
    if (!email) return false;

    return D_ID_EMAIL_SUFFIXES.some(suffix => email.endsWith(suffix));
};

export const isMobile = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return (
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
            userAgent
        ) ||
        /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
            userAgent.substr(0, 4)
        )
    );
};

export const isIOS = () => {
    return /iphone|iPhone|iPod|ipod|iPad|ipad/.test(navigator.userAgent);
};
export const isAndroid = () => {
    return /(android)/i.test(navigator.userAgent);
};

export const isWebview = () => {
    if (typeof window === undefined) {
        return false;
    }

    let navigator: any = window.navigator;

    const standalone = navigator.standalone;
    const userAgent = navigator.userAgent.toLowerCase();
    const safari = /safari/.test(userAgent);
    const ios = /iphone|ipod|ipad/.test(userAgent);

    return ios ? !standalone && !safari : userAgent.includes('wv');
};

export function toBase64(url: string): Promise<string> {
    return new Promise(async (resolve, _reject) => {
        fetch(url)
            .then(response => response.blob())
            .then(blob => {
                var reader = new FileReader();
                reader.onload = function () {
                    resolve(this.result as string);
                };
                reader.readAsDataURL(blob);
            });
    });
}

export interface DownloadData {
    dataurl: string;
    filename: string;
    controller: AbortController;
    extension?: string;
}

export async function bulkDownload(
    filesToDownload: Omit<DownloadData, 'controller'>[],
    controller: AbortController,
    zipName?: string,
    enableIndexing: boolean = true
): Promise<void> {
    const zip = new JSZip();

    const filePromises = filesToDownload.map(async ({ dataurl, filename, extension }, index) => {
        const request = await fetch(dataurl, { signal: controller.signal });
        const blob = await request.blob();

        zip.file(`${filename}${enableIndexing ? `-${index}` : ''}${extension}`, blob);
    });

    await Promise.all(filePromises);

    const zipBlob = await zip.generateAsync({ type: 'blob' });

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(zipBlob);
    link.download = zipName ? `${zipName}.zip` : 'files.zip';
    link.click();
}

export async function download({ dataurl, filename, extension, controller }: DownloadData): Promise<void> {
    const request = await fetch(dataurl, { signal: controller.signal });
    const data = await request.blob();
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(data);
    link.download = filename + (extension ?? '');
    link.click();
}

export function renameFile(originalFile: File, newName: string) {
    const originalSuffix = originalFile.name.split('.').pop();
    return new File([originalFile], `${newName}.${originalSuffix}`, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
    });
}

export function getFileWithSanitizedName(file: File) {
    // Extract the file extension if present
    const extensionMatch = file.name.match(/\.[^/.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : '';

    // Remove the extension from the name for sanitization
    const baseName = file.name.replace(/\.[^/.]+$/, '');

    // Replace special characters in the base name with underscores
    const sanitizedBaseName = baseName.replace(/[^a-zA-Z0-9._-]/g, '_');

    // Ensure the base name is no longer than the max length minus the extension length
    const maxLength = 100 - extension.length;
    const truncatedBaseName = sanitizedBaseName.length > maxLength ? sanitizedBaseName.slice(0, maxLength) : sanitizedBaseName;

    // Return a new File object with the sanitized name
    return new File([file], `${truncatedBaseName}${extension}`, {
        type: file.type,
        lastModified: file.lastModified,
    });
}

export function debounce<T>(func: (data?: T) => any) {
    let timeout: any;

    function executedFunction(wait: number, data?: T): Promise<any> {
        return new Promise(resolve => {
            const later = () => {
                clearTimeout(timeout);
                resolve(func(data));
            };

            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        });
    }

    executedFunction.cancel = () => timeout && clearTimeout(timeout);

    return executedFunction;
}

export const wait = (ms: number) => new Promise(r => setTimeout(r, ms));

export function getImageSize(src: string, retries: number = 3): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.src = src;
        image.onload = () => {
            resolve({
                width: image.width,
                height: image.height,
            });
        };
        image.onerror = async error => {
            if (retries === 0) {
                reject({ message: `failed to load image ${src}`, error });
            } else {
                await wait(200);
                return getImageSize(src, retries - 1);
            }
        };
    });
}

export async function setImageBackground(file: File, backgroundColorHEX = '#FFFFFF'): Promise<File> {
    return new Promise(async resolve => {
        const isPngFile = file.name.endsWith('png');
        if (!isPngFile) {
            resolve(file);
            return;
        }
        const imageSrc = await localFileToURL<string>(file);

        if (!imageSrc) {
            console.error('failed to create image src');
            resolve(file);
            return;
        }

        const image = new Image();
        image.src = imageSrc;
        image.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            canvas.width = image.width;
            canvas.height = image.height;

            if (!ctx) {
                console.error('failed to create canvas');
                resolve(file);
                return;
            }

            ctx.fillStyle = backgroundColorHEX;
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            ctx.globalCompositeOperation = 'source-over';
            ctx.drawImage(image, 0, 0);

            canvas.toBlob(blob => {
                if (blob) {
                    const newFile = new File([blob], file.name, { type: file.type });
                    resolve(newFile);
                } else {
                    console.error('failed to create blob');
                    resolve(file);
                    return;
                }
            }, file.type);
        };
    });
}

export function rgbToHex(r: number, g: number, b: number) {
    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export function hexToRgb(hex: string) {
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (_m, r, g, b) {
        return r + r + g + g + b + b;
    });

    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? { r: parseInt(result[1], 16), g: parseInt(result[2], 16), b: parseInt(result[3], 16) } : { r: 255, g: 255, b: 255 };
}

export async function localFileToURL<T = string | ArrayBuffer | null>(file: any): Promise<T> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result as unknown as T);
        reader.onerror = error => reject(error);
    });
}

export function bytesToMB(bytes: number, round: boolean = false) {
    const mb = bytes / 1000000;
    return round ? Math.round(mb) : Math.round(mb);
}

export function calculateTextWidth(text: string, size: number, font: string = 'Arial') {
    var c = document.createElement('canvas') as HTMLCanvasElement;
    var ctx = c.getContext('2d');
    let width = 0;
    if (ctx) {
        ctx.font = `${size}px ${font}`;
        width = ctx.measureText(text).width;
    }

    c.remove();

    return width;
}

type CompressOptions = {
    compressFormat: string;
    quality: number;
};

export function resizeImage(url: string, width: number, options?: CompressOptions): Promise<string> {
    return new Promise((resolve, reject) => {
        var img = new Image();

        img.onload = async function () {
            const canvas = document.createElement('canvas'),
                ctx = canvas.getContext('2d'),
                oc = document.createElement('canvas'),
                octx = oc.getContext('2d');

            canvas.width = width; // destination canvas size
            canvas.height = (canvas.width * img.height) / img.width;

            let cur = {
                width: Math.floor(img.width * 0.5),
                height: Math.floor(img.height * 0.5),
            };

            oc.width = cur.width;
            oc.height = cur.height;

            if (octx && ctx) {
                octx.drawImage(img, 0, 0, cur.width, cur.height);

                while (cur.width * 0.5 > width) {
                    cur = {
                        width: Math.floor(cur.width * 0.5),
                        height: Math.floor(cur.height * 0.5),
                    };

                    octx.drawImage(oc, 0, 0, cur.width * 2, cur.height * 2, 0, 0, cur.width, cur.height);
                }

                ctx.drawImage(oc, 0, 0, cur.width, cur.height, 0, 0, canvas.width, canvas.height);

                if (!options) {
                    try {
                        const dataUrl = canvas.toDataURL();
                        resolve(dataUrl);
                    } catch (error) {
                        logger.error('[resizeImage] failed to create data url', error);
                        reject(error);
                    }
                }

                try {
                    const dataUrl = canvas.toDataURL(`image/${options?.compressFormat}`, options?.quality);
                    resolve(dataUrl);
                } catch (error) {
                    logger.error('[resizeImage] failed to create data url', error);
                    reject(error);
                }
            }

            canvas.remove();
            oc.remove();
        };

        img.onerror = error => reject(error);

        img.src = url;
    });
}

export function getDiffDays(date1: Date, date2: Date) {
    const diffMilliseconds = date1.getTime() - date2.getTime();
    const millisecondsInDay = 1000 * 60 * 60 * 24;
    return Math.floor(diffMilliseconds % millisecondsInDay);
}

export const shakeAnimation = keyframes`
    10%, 90% { transform: translate3d(-1px, 0, 0); }
    20%, 80% { transform: translate3d(2px, 0, 0); }
    30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
    40%, 60% { transform: translate3d(4px, 0, 0); }
`;

export const isNonPremiumUser = (userPlan: PlanGroup | ProductType | undefined, bypassApiPlans: boolean = false) =>
    checkProductMatch.LITE(userPlan) ||
    (!bypassApiPlans && checkProductMatch.BUILD(userPlan)) ||
    (!bypassApiPlans && checkProductMatch.LAUNCH(userPlan)) ||
    checkProductMatch.GUEST(userPlan) ||
    checkProductMatch.TRIAL(userPlan) ||
    (!bypassApiPlans && checkProductMatch.SCALE(userPlan));

export function hashCode(str: string) {
    let hash = 0;
    for (let i = 0, len = str.length; i < len; i++) {
        let chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}

export const crossSiteDomain = 'd-id.com';

export const getWordsFromText = (text: string, maxWords: number) => {
    const words = text.split(/\s+/);
    let result = '';
    let wordsCount = 0;
    let index = 0;
    while (index <= words.length - 1 && wordsCount < maxWords) {
        result += ` ${words[index]}`;
        if (words[index] !== '⏱') wordsCount++;
        index++;
    }
    return result;
};

export function groupBy<T, G extends string>(items: T[], predicate: (item: T) => G) {
    const grouped = {} as Record<G, T[]>;

    return items.reduce((acc, item) => {
        const key = predicate(item);
        if (!acc[key]) {
            acc[key] = [];
        }

        acc[key].push(item);

        return acc;
    }, grouped);
}

export const toCamelCase = (str?: string) => (str ? str[0].toUpperCase() + str.slice(1) : null);

export const getFileType = (fileType: string): DocumentType => {
    const fileTypeMappings: Record<string, DocumentType | null> = {
        'application/pdf': DocumentType.Pdf,
        'text/plain': DocumentType.Text,
        'text/html': DocumentType.Html,
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': DocumentType.Word,
        'application/json': DocumentType.Json,
        'text/markdown': DocumentType.Markdown,
        'text/csv': DocumentType.Csv,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': DocumentType.Excel,
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': DocumentType.Powerpoint,
        'application/zip': DocumentType.Archive,
        'image/png': DocumentType.Image,
        'audio/*': DocumentType.Audio,
        'video/*': DocumentType.Video,
    };

    return fileTypeMappings[fileType] || DocumentType.Text;
};

export const getVideoMetadata = (file: File): Promise<Pick<HTMLVideoElement, 'duration' | 'videoWidth' | 'videoHeight'>> =>
    new Promise((resolve, reject) => {
        const fileURL = URL.createObjectURL(file);

        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = fileURL;

        video.onloadedmetadata = () => {
            resolve(pick(video, ['duration', 'videoWidth', 'videoHeight']));
            URL.revokeObjectURL(fileURL);
        };
        video.onerror = e => {
            reject(e);
        };
    });

export const capitalizeLanguages = (languages: string[]): string[] =>
    languages
        .map(language => language.trim())
        .filter(language => language !== '' && /^[A-Za-z]/.test(language))
        .map(language => language.charAt(0).toUpperCase() + language.slice(1).toLowerCase());

export const isWordLevelTimestampValid = (wordLevelTimestamps: WordLevelTimestamp[]) => {
    return wordLevelTimestamps.find(word => word.endTime !== word.startTime);
};

export const getItemPresenterTypeAndLabels = (item: LibraryItem) => {
    const labels = item.presenter_labels ?? [];
    if (!item.presenter_version) return { itemType: item.type, itemLabels: labels }; // Like 'translation' video

    if (item.presenter_version.startsWith('clip')) {
        return { itemType: item.presenter_version === 'clip_v2' ? 'premium_plus' : 'premium', itemLabels: labels };
    }

    return { itemType: item.presenter_version, itemLabels: labels }; // 'express' or 'talk' for standard
};

export function remap(value: number, fromMin: number, fromMax: number, toMin: number, toMax: number) {
    return toMin + (toMax - toMin) * ((value - fromMin) / (fromMax - fromMin));
}

export const annoynmousUserToEditorPagePostSsoReturnTo = `${window.location.origin}${AppRoutes.PostSso}?from=advanced`;
